import type { ReactNode } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { CustomDashboardTabGroup } from '@/lib/api/custom-dashboard';
import { TabTable } from './TabTable';

export interface TabGroupCardProps {
  group: CustomDashboardTabGroup;
  tabCount: number;
  isFirst: boolean;
  isLast: boolean;
  /** 配置の保存中など、並べ替え・削除を受け付けないとき。 */
  isLayoutBusy: boolean;
  isEditing: boolean;
  editingName: string;
  isRenaming: boolean;
  onEditingNameChange: (value: string) => void;
  onStartRename: () => void;
  onRenameConfirm: () => void;
  onRenameCancel: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDeleteRequest: () => void;
  onAddTabRequest: () => void;
  /** グループ内のタブの行（`TabRow`）。 */
  children: ReactNode;
}

/**
 * タブグループ 1 つ分のカード。グループの名前変更・並べ替え・削除（中のタブはグループ未指定になる）と、グループ内の
 * タブの一覧表・追加を持つ。
 */
export function TabGroupCard({
  group,
  tabCount,
  isFirst,
  isLast,
  isLayoutBusy,
  isEditing,
  editingName,
  isRenaming,
  onEditingNameChange,
  onStartRename,
  onRenameConfirm,
  onRenameCancel,
  onMoveUp,
  onMoveDown,
  onDeleteRequest,
  onAddTabRequest,
  children,
}: TabGroupCardProps) {
  return (
    <Card role="region" aria-label={`タブグループ ${group.name}`} className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 py-3 pl-5 pr-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isEditing ? (
            <>
              <Input
                aria-label="タブグループ名"
                className="h-8 max-w-xs"
                value={editingName}
                onChange={(event) => onEditingNameChange(event.target.value)}
              />
              <Button
                size="sm"
                onClick={onRenameConfirm}
                disabled={isRenaming || editingName.trim().length === 0}
              >
                確定
              </Button>
              <Button size="sm" variant="ghost" onClick={onRenameCancel} disabled={isRenaming}>
                取消
              </Button>
            </>
          ) : (
            <>
              <h2 className="truncate text-base font-semibold">{group.name}</h2>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`タブグループ ${group.name}の名前を変更`}
                onClick={onStartRename}
              >
                <PencilIcon size={14} aria-hidden />
              </Button>
            </>
          )}
          <span className="whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
            {tabCount} タブ
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            aria-label={`タブグループ ${group.name}を上に移動`}
            disabled={isFirst || isLayoutBusy}
            onClick={onMoveUp}
          >
            <ArrowUpIcon size={16} aria-hidden />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={`タブグループ ${group.name}を下に移動`}
            disabled={isLast || isLayoutBusy}
            onClick={onMoveDown}
          >
            <ArrowDownIcon size={16} aria-hidden />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={`タブグループ ${group.name}を削除`}
            disabled={isLayoutBusy}
            onClick={onDeleteRequest}
          >
            <Trash2Icon size={16} aria-hidden />
          </Button>
        </div>
      </CardHeader>
      {tabCount === 0 ? (
        <p className="border-t px-5 py-4 text-sm text-muted-foreground">
          タブがありません。「このグループにタブを追加」で作成するか、ほかのグループから移動してください。
        </p>
      ) : (
        <TabTable ariaLabel={`タブグループ ${group.name}のタブ`} showOrder>
          {children}
        </TabTable>
      )}
      <button
        type="button"
        aria-label={`タブグループ ${group.name}にタブを追加`}
        onClick={onAddTabRequest}
        className="flex h-11 w-full items-center gap-2 border-t px-5 text-left text-[13px] font-medium text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
      >
        <PlusIcon size={16} aria-hidden />
        このグループにタブを追加
      </button>
    </Card>
  );
}
